// src/utils/mediaPermission.ts
// カメラ/マイクの使用直前に権限を確認するユーティリティ。
// - アプリ内(WebView)  : Flutter(FlutterPermission)へOS権限の確認・要求を依頼し、
//                        未許可ならネイティブ側で通知モーダル（設定誘導）を表示する。
// - ブラウザ閲覧時      : Permissions API で state を確認し、拒否済みなら案内モーダルを表示する。
//
// いずれも「許可されるまで機能へ進めない」ため、呼び出し側は戻り値 false で処理を中断する。
import { Modal } from 'antd';
import { isInFlutter } from './flutterDetection';

export type MediaKind = 'microphone' | 'camera';

const LABEL: Record<MediaKind, string> = {
  microphone: 'マイク',
  camera: 'カメラ',
};

/**
 * アプリ内(WebView)：Flutter に OS 権限の確認・要求を依頼する。
 * 未許可時のモーダル表示・設定アプリ誘導は Flutter 側が行う。
 * 結果は window.__flutterPermissionResult(kind, granted) コールバックで返る。
 */
function ensureViaFlutter(kind: MediaKind): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (granted: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try {
        delete (window as any).__flutterPermissionResult;
      } catch {
        /* no-op */
      }
      resolve(granted);
    };
    // OS プロンプトへのユーザー応答を待つため長めのタイムアウト（安全網）
    timer = setTimeout(() => finish(false), 120000);
    (window as any).__flutterPermissionResult = (k: string, granted: boolean) => {
      if (k === kind) finish(!!granted);
    };
    try {
      const bridge = (window as any).FlutterPermission;
      if (bridge && typeof bridge.postMessage === 'function') {
        bridge.postMessage(kind);
      } else {
        // ブリッジ未提供（旧アプリ等）は従来通り機能側に委ねる
        finish(true);
      }
    } catch {
      finish(true);
    }
  });
}

/** ブラウザ：権限の許可方法を案内するモーダル（設定画面は直接開けないため手順を案内）。 */
function showBrowserGuidance(kind: MediaKind): void {
  const label = LABEL[kind];
  Modal.confirm({
    title: `${label}の使用が許可されていません`,
    content: `${label}を使用するには、ブラウザで${label}へのアクセスを許可する必要があります。`,
    okText: '設定',
    cancelText: '今はおこなわない',
    onOk: () => {
      Modal.info({
        title: `${label}の許可方法`,
        content:
          `ブラウザのアドレスバー左にある「鍵／権限アイコン」を選び、${label}を「許可」に変更してから、` +
          'ページを再読み込みしてください。',
        okText: '閉じる',
      });
    },
  });
}

/** ブラウザ：Permissions API で拒否済みかを確認。拒否なら案内して false。 */
async function ensureViaBrowser(kind: MediaKind): Promise<boolean> {
  try {
    const perms = (navigator as any).permissions;
    if (perms && typeof perms.query === 'function') {
      try {
        const status = await perms.query({ name: kind as PermissionName });
        if (status && status.state === 'denied') {
          showBrowserGuidance(kind);
          return false;
        }
      } catch {
        // camera/microphone の query 未対応ブラウザは判定不能 → 続行し getUserMedia に委ねる
      }
    }
    return true; // granted / prompt → 機能側の getUserMedia でプロンプト・確定
  } catch {
    return true;
  }
}

/**
 * 使用直前に呼ぶ。許可されていれば true。未許可なら通知モーダルを表示し false。
 * false のときは機能へ進めないこと。
 */
export async function ensureMediaPermission(kind: MediaKind): Promise<boolean> {
  if (isInFlutter()) return ensureViaFlutter(kind);
  return ensureViaBrowser(kind);
}

/**
 * ブラウザで getUserMedia が拒否された場合に案内モーダルを出す（機能側の catch から呼ぶ）。
 * アプリ内はネイティブ側で表示済みのため何もしない。
 */
export function notifyMediaDenied(kind: MediaKind): void {
  if (isInFlutter()) return;
  showBrowserGuidance(kind);
}
